import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import {
  FaEdit,
  FaTools,
  FaInfoCircle,
  FaPlus,
  FaArrowLeft,
  FaFileDownload,
  FaTable,
  FaChartPie,
  FaTachometerAlt,
  FaSearch,
  FaFilter,
} from "react-icons/fa";

import * as XLSX from "xlsx";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import "./BreakdownTracker1.css";

const BreakdownTracker = () => {
  const [breakdowns, setBreakdowns] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("IN_TRANSIT");
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [activeTab, setActiveTab] = useState("table");
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchBreakdownRecords();
  }, []);

  const fetchBreakdownRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/breakdownRecords`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const normalize = (rec) => {
        const out = {};
        Object.keys(rec || {}).forEach((k) => {
          out[k.toLowerCase()] = rec[k];
        });
        return out;
      };

      setBreakdowns((response.data || []).map(normalize));
    } catch (error) {
      console.error("Error fetching breakdown records:", error);
      setBreakdowns([]);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const filteredBreakdowns = useMemo(() => {
    return breakdowns
      .filter((breakdown) =>
        selectedStatus ? breakdown.jp_status === selectedStatus : true
      )
      .filter((breakdown) =>
        globalFilter
          ? Object.values(breakdown)
              .join(" ")
              .toLowerCase()
              .includes(globalFilter.toLowerCase())
          : true
      );
  }, [breakdowns, selectedStatus, globalFilter]);

  // Chart data processing (use lowercase keys)
  const chartData = useMemo(() => {
    if (!filteredBreakdowns.length) return { pieData: [], barData: [] };

    const issueCount = filteredBreakdowns.reduce((acc, record) => {
      const issue = record.issue || "Unknown";
      acc[issue] = (acc[issue] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.keys(issueCount).map((issue) => ({
      name: issue,
      value: issueCount[issue],
      percentage: (
        (issueCount[issue] / filteredBreakdowns.length) *
        100
      ).toFixed(1),
    }));

    const carrierCount = filteredBreakdowns.reduce((acc, record) => {
      const carrier = record.carrier || "Unknown";
      acc[carrier] = (acc[carrier] || 0) + 1;
      return acc;
    }, {});

    const barData = Object.keys(carrierCount).map((carrier) => ({
      name: carrier,
      count: carrierCount[carrier],
    }));

    return { pieData, barData };
  }, [filteredBreakdowns]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return moment(date).format("DD/MM/YYYY HH:mm");
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "JP Number",
        accessor: "journey_plane_no",
        width: 120,
      },
      {
        Header: "Truck Number",
        accessor: "scheduled_vehicle",
        width: 120,
      },
      {
        Header: "Breakdown Date",
        accessor: "breakdown_time",
        Cell: ({ value }) => formatDateTime(value),
        width: 150,
      },
      {
        Header: "Driver Name",
        accessor: "driver_name",
        width: 150,
      },
      {
        Header: "Driver Phone",
        accessor: "driver_phone",
        Cell: ({ value }) => value || "N/A",
        width: 130,
      },
      {
        Header: "Carrier",
        accessor: "carrier",
        width: 100,
      },
      {
        Header: "Check Point",
        accessor: "ivms_point",
        Cell: ({ value }) => value || "N/A",
        width: 120,
      },
      {
        Header: "Issue",
        accessor: "issue",
        width: 100,
      },
      {
        Header: "Truck Status",
        accessor: "truck_status",
        width: 120,
      },
      {
        Header: "Breakdown Status",
        accessor: "breakdow_status",
        Cell: ({ value }) => (
          <div className="bt-status-badge">{value || "Pending"}</div>
        ),
        width: 140,
      },
      {
        Header: "JP Status",
        accessor: "jp_status",
        Cell: ({ value }) => (
          <div className={`bt-jp-status ${value?.toLowerCase()}`}>
            {value?.replace(/_/g, " ") || "N/A"}
          </div>
        ),
        width: 120,
      },
      {
        Header: "Date Resolved",
        accessor: "date_resolved",
        Cell: ({ value }) => formatDateTime(value),
        width: 150,
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="bt-action-icons">
            <button
              className="bt-action-btn bt-edit-btn"
              title="Edit"
              onClick={() =>
                navigate(`/edit-breakdown/${row.original.breakdown_id}`)
              }
            >
              <FaEdit />
            </button>
            <button
              className="bt-action-btn bt-handle-btn"
              title="Handle"
              onClick={() =>
                navigate(`/handle-breakdown/${row.original.breakdown_id}`)
              }
            >
              <FaTools />
            </button>
            <button
              className="bt-action-btn bt-info-btn"
              title="Info"
              onClick={() =>
                navigate(`/info-breakdown/${row.original.breakdown_id}`)
              }
            >
              <FaInfoCircle />
            </button>
          </div>
        ),
        width: 120,
      },
    ],
    [navigate]
  );

  const data = React.useMemo(() => filteredBreakdowns, [filteredBreakdowns]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data,
      initialState: {
        pageSize: 50,
      },
    });

  const handleExport = () => {
    if (
      window.confirm(
        `Are you sure you want to export ${filteredBreakdowns.length} records to Excel?`
      )
    ) {
      const exportData = filteredBreakdowns.map((breakdown) => ({
        "JP Number": breakdown.journey_plane_no,
        "Truck Number": breakdown.scheduled_vehicle,
        "Breakdown Date": formatDateTime(breakdown.breakdown_time),
        "Driver Name": breakdown.driver_name,
        "Driver Phone": breakdown.driver_phone || "N/A",
        Carrier: breakdown.carrier,
        "Last Check Point": breakdown.ivms_point || "N/A",
        Issue: breakdown.issue,
        "Truck Status": breakdown.truck_status,
        "Breakdown Status": breakdown.breakdow_status,
        "JP Status": breakdown.jp_status?.replace(/_/g, " ") || "N/A",
        "Date Resolved": formatDateTime(breakdown.date_resolved),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Breakdowns");

      const status = selectedStatus || "ALL";
      const date = new Date().toISOString().split("T")[0];
      const fileName = `Breakdowns_${status}_${date}.xlsx`;

      XLSX.writeFile(wb, fileName);
    }
  };

  // Color palettes for charts
  const PIE_COLORS = [
    "#4f46e5",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
  ];
  const BAR_COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  const hasData = filteredBreakdowns.length > 0;

  return (
    <div className="bt-container">
      {/* Header */}
      {/* Header */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            {/* This is the back arrow button */}
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back to Main Page"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>Breakdown Tracker</h1>
              <p>Manage and analyze vehicle breakdown records</p>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="bt-status-filter"
              >
                <option value="">All Status</option>
                <option value="IN_TRANSIT">IN TRANSIT</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bt-main-content">
        {/* Action Bar */}
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button
              onClick={() => navigate("/add-breakdown")}
              className="bt-primary-btn"
            >
              <FaPlus />
              Add New Breakdown
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-export-btn"
              onClick={handleExport}
              disabled={!hasData}
            >
              <FaFileDownload />
              Export Excel
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bt-tab-nav">
          <button
            className={`bt-tab-btn ${
              activeTab === "table" ? "bt-tab-active" : ""
            }`}
            onClick={() => setActiveTab("table")}
          >
            <FaTable />
            Breakdown List
            <span className="bt-tab-count">{filteredBreakdowns.length}</span>
          </button>
          <button
            className={`bt-tab-btn ${
              activeTab === "dashboard" ? "bt-tab-active" : ""
            }`}
            onClick={() => setActiveTab("dashboard")}
            disabled={!hasData}
          >
            <FaChartPie />
            Analytics Dashboard
          </button>
        </div>

        {/* Table View */}
        {activeTab === "table" && (
          <div className="bt-table-section">
            <div className="bt-table-container">
              {hasData ? (
                <table {...getTableProps()} className="bt-data-table">
                  <thead className="bt-table-header">
                    {headerGroups.map((headerGroup, headerIndex) => (
                      <tr
                        {...headerGroup.getHeaderGroupProps()}
                        key={`headerGroup-${headerIndex}`}
                      >
                        {headerGroup.headers.map((column, columnIndex) => (
                          <th
                            {...column.getHeaderProps()}
                            key={`column-${headerIndex}-${columnIndex}`}
                            className="bt-table-head"
                            style={{ width: column.width }}
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()} className="bt-table-body">
                    {rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          key={row.original.breakdown_id}
                          className="bt-table-row"
                        >
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={`${row.original.breakdown_id}-${cell.column.id}`}
                              className="bt-table-cell"
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="bt-empty-state">
                  <FaTachometerAlt className="bt-empty-icon" />
                  <h3>No breakdown records found</h3>
                  <p>
                    Try adjusting your search or filter criteria, or add new
                    breakdown records
                  </p>
                  <button
                    onClick={() => navigate("/add-breakdown")}
                    className="bt-primary-btn"
                  >
                    <FaPlus />
                    Add First Breakdown
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboard View */}
        {activeTab === "dashboard" && (
          <div className="bt-dashboard-section">
            {hasData ? (
              <>
                {/* Stats Overview */}
                <div className="bt-stats-overview">
                  <div className="bt-stat-card">
                    <div className="bt-stat-icon total">
                      <FaTachometerAlt />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Total Breakdowns</h3>
                      <span className="bt-stat-value">
                        {filteredBreakdowns.length}
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon in-transit">
                      <FaTools />
                    </div>
                    <div className="bt-stat-info">
                      <h3>In Transit</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter(
                            (b) => b.jp_status === "IN_TRANSIT"
                          ).length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon resolved">
                      <FaInfoCircle />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Resolved</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter((b) => b.resolved === "Y")
                            .length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon pending">
                      <FaChartPie />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Pending</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter((b) => b.resolved !== "Y")
                            .length
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Charts */}
                <div className="bt-charts-grid">
                  <div className="bt-chart-card">
                    <div className="bt-chart-header">
                      <h3>Breakdown Type Distribution</h3>
                    </div>
                    <div className="bt-chart-container">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={chartData.pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percentage }) =>
                              `${name}: ${percentage}%`
                            }
                            outerRadius={100}
                            innerRadius={60}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {chartData.pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => [
                              `${value} (${props.payload.percentage}%)`,
                              name,
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bt-chart-card">
                    <div className="bt-chart-header">
                      <h3>Breakdowns by Carrier</h3>
                    </div>
                    <div className="bt-chart-container">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={chartData.barData}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f3f4f6"
                          />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="count"
                            name="Number of Breakdowns"
                            fill={BAR_COLORS[0]}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bt-empty-dashboard">
                <FaChartPie className="bt-empty-icon" />
                <h3>No data available for analysis</h3>
                <p>Add breakdown records to view analytics dashboard</p>
                <button
                  onClick={() => navigate("/add-breakdown")}
                  className="bt-primary-btn"
                >
                  <FaPlus />
                  Add Breakdown Record
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakdownTracker;
